/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Entity,
  RELATION_CHILD_OF,
  RELATION_PART_OF,
  parseEntityRef,
} from '@backstage/catalog-model';

export type ParentGroupDetails = {
  ref: string;
  label: string;
  color: string;
};

const PARENT_GROUP_COLORS = [
  '#E1F5FE',
  '#E8F5E9',
  '#FFF3E0',
  '#F3E5F5',
  '#EDE7F6',
  '#FCE4EC',
];

const hashString = (input: string): number => {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return hash;
};

const selectParentColor = (ref: string): string => {
  const normalized = ref.toLocaleLowerCase('en-US');
  const paletteIndex =
    Math.abs(hashString(normalized)) % PARENT_GROUP_COLORS.length;
  return PARENT_GROUP_COLORS[paletteIndex];
};

export const formatParentGroupLabel = (ref: string): string => {
  try {
    const { kind, namespace, name } = parseEntityRef(ref);
    const namespaceSuffix =
      namespace && namespace !== 'default' ? ` (${namespace})` : '';
    const normalizedKind = kind
      ? `${kind.charAt(0).toUpperCase()}${kind
          .slice(1)
          .toLocaleLowerCase('en-US')}`
      : 'Entity';
    return `${normalizedKind} · ${name}${namespaceSuffix}`;
  } catch (error) {
    return ref;
  }
};

export const deriveParentGroup = (
  entity: Entity,
): ParentGroupDetails | undefined => {
  let parentRelation = entity.relations?.find(
    relation =>
      relation.type === RELATION_CHILD_OF || relation.type === RELATION_PART_OF,
  );

  // Handle API entities that have a apiProvidedBy spec property - only if no other parent relations exist
  if (
    entity.kind.toLowerCase() === 'api' &&
    entity.spec &&
    entity.spec.apiProvidedBy
  ) {
    // Create a temporary relation object for apiProvidedBy
    parentRelation = {
      targetRef: entity.spec.apiProvidedBy.toString(),
      type: RELATION_CHILD_OF,
    };
  }

  if (!parentRelation?.targetRef) {
    return undefined;
  }

  const parentRef = parentRelation.targetRef;

  const result = {
    ref: parentRef,
    label: formatParentGroupLabel(parentRef),
    color: selectParentColor(parentRef),
  };

  return result;
};
